import {
  Bot,
  CornerUpLeft,
  FileText,
  Forward,
  Lock,
  Paperclip,
  ReplyAll,
  Send,
  Smile,
  Sparkles,
  Tag,
  Users,
  X,
} from 'lucide-react';
import { type CSSProperties, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { OutgoingAttachment } from '../../api/comms';
import { ApiError } from '../../api/envelope';
import type {
  CannedResponse,
  Conversation,
  ConversationDraft,
  Inbox,
  Message,
  TemplateBlock,
  WhatsappTemplateSummary,
} from '../../api/types';
import { escapeToHtml, smsCount } from '../../lib/commsFormat';
import { useTimeFormat } from '../../lib/tenantTime';
import {
  contactEmailOf,
  dedupeAddresses,
  forwardQuote,
  forwardSubject,
  hasOthers,
  replyRecipients,
  type ReplyTargets,
  subjectOf,
  withoutForwardQuote,
} from '../../lib/emailRecipients';
import {
  channelKeyForInbox,
  CHANNEL_META,
  COMPOSER_MAX_HEIGHT,
  COMPOSER_MAX_HEIGHT_DEFAULT,
  INBOX_ICONS,
  inboxIconKey,
} from '../../lib/commsMeta';
import { identityLine, needsReauth, reauthFixHint } from '../../lib/channelMeta';
import { capabilitiesOf } from '../../lib/channelCapabilities';
import { EMOJI } from '../../lib/emoji';
import { useDismissOnOutside } from '../../lib/useDismiss';
import {
  emptyDraft,
  missingFields,
  renderPreview,
  toTemplateParams,
  type WhatsappTemplateDraft,
} from '../../lib/whatsappTemplates';
import {
  type BufferedDraft,
  type DraftBody,
  isBlankDraft,
  newerDraft,
  readComposerDraft,
  saveComposerDraft,
  setDraftSummary,
} from './drafts';
import { RecipientFields, type RecipientLists } from './RecipientFields';
import { RichText, type RichTextHandle, type RichTextValue } from './RichText';
import { WhatsappTemplatePanel } from './WhatsappTemplatePanel';

/**
 * How long typing settles before the draft goes to the server. Long enough that
 * a sentence is one write rather than forty, short enough that a browser closed
 * without an unmount (a crash, a killed tab) loses only the tail — which the
 * local buffer has anyway.
 */
const SAVE_DEBOUNCE_MS = 900;

interface PendingTemplate {
  summary: WhatsappTemplateSummary;
  draft: WhatsappTemplateDraft;
}

/**
 * What the send button does on a channel that can address more than one person.
 *
 * `reply` and `replyAll` both send on the click that names them — a button
 * labelled "Reply all" that replies to all is what the agent asked for, and it
 * is only offered when the thread actually has somebody else on it. `forward`
 * cannot: it has no recipient until one is typed, so clicking it switches the
 * composer INTO forward mode (empty To, `Fwd:` subject, the original quoted
 * below) and the send happens on the second click, against a To the agent can
 * see. Every other channel stays on `reply` and reads "Send".
 */
type SendMode = 'reply' | 'replyAll' | 'forward';

const emptyLists = (): RecipientLists => ({ to: [], cc: [], bcc: [] });

function fileType(file: File): OutgoingAttachment['fileType'] {
  if (file.type.startsWith('image/')) return 'image';
  if (file.type.startsWith('audio/')) return 'audio';
  if (file.type.startsWith('video/')) return 'video';
  return 'file';
}

/**
 * A forward: nobody addressed yet, the thread's title under a `Fwd:` prefix,
 * and the message being forwarded quoted below so the agent writes above it.
 * The quote is plain text even in the styled editor — see forwardQuote for why
 * the sender's own html does not come along.
 *
 * One function for both ways in (the button in the composer, and a mail-client
 * layout that opened straight into one), because a forward that arrives with a
 * subject but no quote is the kind of difference nobody notices until a
 * customer reads it.
 */
function forwardDraft(
  targets: ReplyTargets,
  threadSubject: string,
  sentAt: (iso: string) => string,
  existing: string,
): { subject: string; lists: RecipientLists; body: string } {
  const source = targets.lastInbound;
  const subject = forwardSubject(source ? subjectOf(source) || threadSubject : threadSubject);
  const quote = source
    ? forwardQuote(source, {
        fallbackTo: targets.contactEmail,
        sentAt: sentAt(source.externalCreatedAt ?? source.createdAt),
      })
    : '';
  const above = existing.trim();
  return {
    subject,
    lists: { to: [], cc: [], bcc: [] },
    body: above ? `${above}\n\n${quote}` : quote,
  };
}

/**
 * A STORED draft that is a forward somebody actually wrote in.
 *
 * A forward opened and left alone is a quote this composer inserted and a title
 * it wrote — nothing of the agent's. Restoring one puts them in front of a
 * `Fwd:` addressed to nobody on a thread they never typed in, and the
 * three-pane layout has no Reply button to say otherwise: it passes no
 * `openAs`, the box is simply there, so the mode can only come from the draft
 * and Cancel is the only way out.
 *
 * The quote-less body is the test, so words written ABOVE it still count, as do
 * files. It cannot see words typed UNDER the quote — nothing stored can — which
 * is why the live box asks a stricter question (see `insertedQuote`); this one
 * only has to judge rows already written to disk.
 */
const writtenForward = (draft: BufferedDraft | ConversationDraft | null): boolean =>
  !!draft?.subject && !isBlankDraft({ ...draft, content: withoutForwardQuote(draft.content) });

/** The body that says "there is nothing here": draftSave deletes the row on it. */
const clearedBody = (isNote: boolean): DraftBody => ({
  content: '',
  bodyHtml: null,
  isNote,
  attachments: [],
  toEmails: [],
  ccEmails: [],
  bccEmails: [],
  subject: null,
});

/**
 * What `openAs` makes of a freshly mounted composer — see the prop.
 *
 * Computed into the initial state rather than applied by a mount effect, and
 * that is not only about cascading renders: the composer decides it has been
 * TOUCHED by comparing its draft against the one it mounted holding, so a
 * forward set up a tick later would look like typing and PUT a draft nobody
 * wrote. Returns null for the ordinary case, which is every mount from the
 * three-pane layout.
 *
 * The button WINS over the draft about what this box IS. A draft left here as a
 * forward would otherwise reopen as one under a Reply the agent just pressed —
 * subject row, `Fwd:` title, addressed to nobody, and the thread's Cc gone —
 * which is the bug this rule exists to prevent. What was WRITTEN still comes
 * back, because those are the agent's words; `recipientsOpen` below is forced
 * whenever an opening changed anything, so where it now goes is on screen
 * before it can be sent.
 */
function openingState(
  openAs: SendMode | undefined,
  buffered: BufferedDraft | null,
  targets: ReplyTargets,
  threadSubject: string,
  sentAt: (iso: string) => string,
): { mode: SendMode; subject: string; lists: RecipientLists; body: string | null } | null {
  if (!openAs) return null;
  /** What the restored draft addressed, which a reply keeps and a forward drops. */
  const held: RecipientLists = {
    to: buffered?.toEmails ?? [],
    cc: buffered?.ccEmails ?? [],
    bcc: buffered?.bccEmails ?? [],
  };
  if (openAs === 'forward') {
    const forward = forwardDraft(targets, threadSubject, sentAt, buffered?.content ?? '');
    return { mode: 'forward', subject: forward.subject, lists: forward.lists, body: forward.body };
  }
  if (openAs === 'reply') {
    // Nothing to say about an ordinary Reply over an ordinary draft — that is
    // the mount every layout does, and returning null leaves it exactly alone.
    return buffered?.subject ? { mode: 'reply', subject: '', lists: held, body: null } : null;
  }
  const audience = replyRecipients(targets, 'replyAll');
  const to = held.to.length ? held.to : audience.to;
  return {
    mode: 'reply',
    subject: '',
    lists: {
      // MERGED, not replaced. A draft addressed by hand is the agent's own
      // answer to this question, but Reply all is a second answer that ADDS to
      // it — the same merge the Reply all send button does. Bailing out instead
      // (which is what this did) meant one address typed yesterday, or a To
      // chip the Recipients toggle fills in by itself, silently cost the whole
      // thread's Cc today.
      to,
      cc: dedupeAddresses([...held.cc, ...audience.cc], [...to, ...held.bcc]),
      bcc: held.bcc,
    },
    body: null,
  };
}

/** The reply box under a thread — templates, AI drafts, notes, attachments. */
export function Composer({
  conversation,
  inbox,
  canned,
  blocks,
  lastInboundAt,
  lastInbound,
  threadSubject,
  draftKey,
  openAs,
  api,
  onSend,
  onSendAndClose,
}: {
  conversation: Conversation;
  inbox: Inbox | undefined;
  canned: CannedResponse[];
  /**
   * The dashboard's saved email blocks. Offered only on a styled channel — a
   * letterhead pasted into an SMS is its markup, spelled out.
   */
  blocks: TemplateBlock[];
  /** Timestamp of the newest inbound message, for channels with a reply window. */
  lastInboundAt: string | null;
  /**
   * The newest inbound message itself, which is what "all" means on a Reply all
   * — its To and Cc are the people this thread reached. Undefined on a thread
   * this console opened and the contact has not answered.
   */
  lastInbound: Message | undefined;
  /** The thread's own title, prefixed into `Fwd: …` when forwarding. */
  threadSubject: string;
  /**
   * Where this browser keeps what is left half-written here — see
   * `composerDraftKey`. One per conversation, and ThreadPane keys this
   * component by the same conversation, so the key is fixed for an instance's
   * whole life.
   */
  draftKey: string;
  /**
   * The button that opened this box, applied once on mount.
   *
   * The mail-client layout ends a thread in three buttons rather than a box, so
   * pressing one of them has to be the first half of that action — a Forward
   * that opened addressed to the contact would be a forward in name only.
   * Undefined leaves the composer exactly as it has always opened, which is
   * what the three-pane layout passes: there the box is simply there.
   */
  openAs?: 'reply' | 'replyAll' | 'forward';
  api: {
    /** The agent's own unsent reply on this conversation, or null. */
    getDraft: (displayId: number) => Promise<{ draft: ConversationDraft | null }>;
    saveDraft: (displayId: number, body: DraftBody) => Promise<{ draft: ConversationDraft | null }>;
    draftReply: (displayId: number, instruction?: string) => Promise<{ draft: string }>;
    presignUpload: (fileName: string, contentType: string) => Promise<{ s3Key: string; putUrl: string }>;
    putToS3: (putUrl: string, file: File) => Promise<void>;
  };
  onSend: (body: {
    content: string;
    bodyHtml?: string;
    isNote: boolean;
    attachments: OutgoingAttachment[];
    contentAttributes?: Record<string, unknown>;
  }) => Promise<void>;
  onSendAndClose: () => Promise<void>;
}) {
  /**
   * What this browser buffered. Read once, on mount, and applied synchronously
   * so the box is never briefly empty in front of the agent: the server copy
   * arrives a round trip later and only replaces this if it is newer AND
   * nothing has been typed since (see the load effect below).
   */
  const [stored] = useState(() => readComposerDraft(draftKey));
  /**
   * ...minus a forward nobody wrote in, which is not a draft at all — see
   * `writtenForward`. Dropped here rather than at each of the ten initialisers
   * below, so the box opens as the plain reply it would have been; the row
   * itself is then cleared by the save effect, which sees an empty composer and
   * a `stored` row to clear.
   */
  const buffered = writtenForward(stored) || !stored?.subject ? stored : null;
  const { fmtDateTime } = useTimeFormat();
  /**
   * The button that opened this box, once the channel has been asked whether it
   * could have been pressed at all. Only where a second address field exists:
   * on SMS there is one recipient, so neither button is drawn. A buffered NOTE
   * outranks all three — restoring a private line into a reply box is the one
   * way drafts could make things worse, so the note comes back and the button
   * is dropped.
   *
   * Frozen at mount, because both the opening below and the draft load further
   * down have to answer to the SAME button.
   */
  const [openMode] = useState<SendMode | undefined>(() =>
    capabilitiesOf(inbox).cc && !buffered?.isNote ? openAs : undefined,
  );
  const [opening] = useState(() =>
    openingState(
      openMode,
      buffered,
      {
        contactEmail: contactEmailOf(conversation),
        ownEmail: inbox?.channelType === 'email' ? (inbox.channel.email ?? null) : null,
        lastInbound,
      },
      threadSubject,
      // The tenant's own clock, like every other time in the thread — an ISO
      // string in a mail somebody else will read is not a date.
      fmtDateTime,
    ),
  );
  // Both boxes, not the one this channel happens to use: the note toggle
  // switches between them, and only the ACTIVE one is ever saved back.
  const [text, setText] = useState(opening?.body ?? buffered?.content ?? '');
  const [rich, setRich] = useState<RichTextValue>(() => {
    const body = opening?.body ?? buffered?.content ?? '';
    if (opening?.body) return { html: escapeToHtml(opening.body), text: body };
    return {
      html: buffered ? (buffered.bodyHtml ?? escapeToHtml(buffered.content)) : '',
      text: body,
    };
  });
  const [plainEmoji, setPlainEmoji] = useState(false);
  // Note mode comes back with the words — a private line restored into the
  // reply box is the one way this feature could make things worse.
  const [isNote, setIsNote] = useState(buffered?.isNote ?? false);
  const [attachments, setAttachments] = useState<OutgoingAttachment[]>(buffered?.attachments ?? []);
  /**
   * Who this reply is addressed to. Restored from the buffer for the same reason
   * the words are: a draft that comes back complete but addressed to one person
   * is worse than one that comes back empty.
   */
  const [recipients, setRecipients] = useState<RecipientLists>(
    () =>
      opening?.lists ?? {
        to: buffered?.toEmails ?? [],
        cc: buffered?.ccEmails ?? [],
        bcc: buffered?.bccEmails ?? [],
      },
  );
  // A buffered draft that named its own subject was a forward in progress —
  // that field is the only thing distinguishing one from a reply.
  const [mode, setMode] = useState<SendMode>(
    opening?.mode ?? (buffered?.subject ? 'forward' : 'reply'),
  );
  // `opening ? …` rather than `opening?.subject || …`: an opening that cleared
  // the subject is an answer, and falling through to the draft's would leave a
  // `Fwd:` title behind a reply the moment the agent pressed the Forward button.
  const [forwardSubjectLine, setForwardSubjectLine] = useState(
    opening ? opening.subject : (buffered?.subject ?? ''),
  );
  const [recipientsOpen, setRecipientsOpen] = useState(
    () =>
      !!opening ||
      !!buffered?.subject ||
      !!buffered?.ccEmails?.length ||
      !!buffered?.bccEmails?.length,
  );
  const [recipientFocus, setRecipientFocus] = useState<keyof RecipientLists>(
    opening?.mode === 'forward' ? 'to' : 'cc',
  );
  /**
   * Bumped whenever the lists are replaced from outside the fields — a forward,
   * a cancel, a send. It remounts them, which is what clears an address that was
   * TYPED and never became a chip: that text is the field's own state, and a
   * rejected one left standing would be committed into the next message.
   */
  const [recipientsKey, setRecipientsKey] = useState(0);
  /**
   * The quote this composer put in the box, verbatim.
   *
   * `withoutForwardQuote` can only see what was written ABOVE the quote, and a
   * forward written UNDER it is somebody's work all the same. Comparing against
   * what was actually inserted answers "has this been touched" exactly, for the
   * one box where the answer is knowable. Empty for a forward restored from a
   * draft — that one was written by definition, or it would not be here.
   *
   * State rather than a ref because `unwritten` below is derived from it during
   * render, and that is what decides whether this box is saved at all.
   */
  const [insertedQuote, setInsertedQuote] = useState(
    opening?.mode === 'forward' ? (opening.body ?? '') : '',
  );
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [template, setTemplate] = useState<PendingTemplate | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useDismissOnOutside<HTMLDivElement>(menuOpen, () => setMenuOpen(false));
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const bodyEditor = useRef<RichTextHandle>(null);
  const bodyBox = useRef<HTMLTextAreaElement>(null);

  const caps = capabilitiesOf(inbox);
  // Notes are stored and shown as plain text, so styling them would be a lie.
  const styled = caps.richText && !isNote;
  const body = styled ? rich.text : text;
  const channelKey = inbox ? channelKeyForInbox(inbox) : 'all';
  const meta = CHANNEL_META[channelKey];
  const ChannelIcon = inbox ? INBOX_ICONS[inboxIconKey(inbox)] : meta.icon;
  const sendingAs = inbox ? identityLine(inbox) : '';
  const isSmsLike = channelKey === 'sms';
  const waChannel = inbox?.channelType === 'whatsapp' ? inbox.channel : null;
  const waTemplates = waChannel?.templates ?? [];
  // Messenger and Instagram keep the same clock, and nothing reopens it: past
  // the window the customer has to write first, so the box closes with that.
  const metaChannel =
    inbox?.channelType === 'facebook' || inbox?.channelType === 'instagram' ? inbox.channel : null;
  const windowedChannel = waChannel ?? metaChannel;
  const windowHours = windowedChannel?.sessionWindowHours ?? 24;
  // Blocks are html, so they are only offered where html is what gets sent. On a
  // note they are wrong for a second reason: a note is stored and shown as plain
  // text, so a letterhead would appear as its own markup.
  const offeredBlocks = styled ? blocks : [];

  /**
   * Reply / Reply all / Forward replace Send only where they mean something:
   * an SMS has one recipient and no second field to put anybody in.
   */
  const addressable = caps.cc && !isNote;
  const contactEmail = contactEmailOf(conversation);
  const ownEmail = inbox?.channelType === 'email' ? (inbox.channel.email ?? null) : null;
  const replyTargets = useMemo(
    () => ({ contactEmail, ownEmail, lastInbound }),
    [contactEmail, ownEmail, lastInbound],
  );
  const canReplyAll = addressable && mode !== 'forward' && hasOthers(replyTargets);
  const forwarding = addressable && mode === 'forward';

  /**
   * The To an ordinary reply goes to when the agent has named nobody. Left OUT
   * of the request in that case rather than filled in here: the server resolves
   * the contact's address itself, and a stale copy of it typed into a draft
   * yesterday should not outrank the contact record today.
   */
  const effectiveTo = recipients.to.length ? recipients.to : contactEmail ? [contactEmail] : [];
  const extraCount = recipients.cc.length + recipients.bcc.length;

  const count = useMemo(() => {
    if (!isSmsLike || isNote) return null;
    return smsCount(text.length);
  }, [isSmsLike, isNote, text.length]);

  /**
   * How far this box may grow. A note is bounded by nothing the channel says —
   * it is never delivered — so it takes the default rather than the SMS ceiling
   * it would inherit from the thread it sits on.
   */
  const maxBodyHeight =
    (isNote ? undefined : COMPOSER_MAX_HEIGHT[channelKey]) ?? COMPOSER_MAX_HEIGHT_DEFAULT;

  /**
   * Grow the plain box with what is in it, up to that ceiling.
   *
   * The height is measured from `auto` rather than adjusted from where it
   * stands: scrollHeight reports the content OR the current height, whichever
   * is larger, so a box that is not reset first can only ever grow — deleting
   * three lines would leave the hole they left. Layout, not effect: a paint at
   * the old height is a visible jump on every keystroke that wraps.
   *
   * The styled editor needs none of this. A contenteditable is sized by its own
   * content already, and reads the same ceiling straight off `--rt-max` below.
   */
  useLayoutEffect(() => {
    const node = bodyBox.current;
    if (!node) return;
    node.style.height = 'auto';
    node.style.height = `${Math.min(node.scrollHeight, maxBodyHeight)}px`;
  }, [text, maxBodyHeight]);

  /**
   * The draft as it stands: the ACTIVE box only, so the other one's leftovers
   * can never be saved over what is on screen.
   */
  const draft = useMemo<DraftBody>(
    () => ({
      content: styled ? rich.text : text,
      bodyHtml: styled ? rich.html : null,
      isNote,
      attachments,
      toEmails: recipients.to,
      ccEmails: recipients.cc,
      bccEmails: recipients.bcc,
      subject: mode === 'forward' ? forwardSubjectLine : null,
    }),
    [styled, rich.html, rich.text, text, isNote, attachments, recipients, mode, forwardSubjectLine],
  );

  /**
   * Is any of what is in this box the AGENT's?
   *
   * `isBlankDraft` asks whether there are words, which is the whole answer for a
   * reply. A FORWARD arrives pre-filled — the quoted original, a `Fwd:` title,
   * neither of them typed — so that question calls it a draft the instant the
   * button is pressed, and one pressed and abandoned then comes back as a
   * forward the next time the thread is opened. See `writtenForward` for why
   * the three-pane layout is where that bites.
   *
   * A forward is measured against the quote as INSERTED, so an edit anywhere
   * counts, under it included. A recipient with nothing written is not a draft,
   * the same rule a reply keeps — and the send refuses an empty body anyway.
   */
  const unwritten =
    mode === 'forward'
      ? attachments.length === 0 && body.trim() === insertedQuote.trim()
      : isBlankDraft(draft);

  /**
   * Has the agent touched this composer since it opened?
   *
   * Derived by comparing the draft with the one it mounted holding, rather than
   * set by hand from each of the dozen places that change it — a `touch()` the
   * emoji picker or the block inserter forgot to call would silently stop
   * saving, and nothing on screen would say so.
   *
   * Two things read it. The server's copy may arrive after typing has started —
   * a laptop left open on the same thread — and overwriting a live sentence
   * with an older saved one is worse than the staleness it would fix. And an
   * untouched composer must not PUT an empty draft over a row written on
   * another machine.
   */
  const signature = JSON.stringify(draft);
  const [mountSignature] = useState(() => signature);
  const dirty = signature !== mountSignature;
  // The load below resolves long after its effect ran, so it needs the answer
  // as it stands then, not as it stood at mount.
  const dirtyRef = useRef(false);
  /**
   * Has the agent's own content EVER been in this box?
   *
   * `dirty` answers for the moment, and two things put a box back to "untouched"
   * by its measure: a send that emptied it, and the words deleted by hand. Blank
   * is how it opened, so to the signature both look like a composer that has
   * only just mounted — and that one must be left alone (a row written at
   * another desk may be on the server, not yet asked for), while these two have
   * things to take back: the "Draft" the list was told while typing, a save the
   * debounce had not flushed, the row itself. The save effect tells them apart
   * by this.
   */
  const touchedRef = useRef(false);
  useEffect(() => {
    dirtyRef.current = dirty;
    if (dirty) touchedRef.current = true;
  }, [dirty]);

  /**
   * The server's copy, which is the record of truth: it followed the agent from
   * whatever machine they last typed on, and the Drafts view is built from it.
   *
   * Skipped entirely when the row this composer opened from already said there
   * is no draft here AND this browser buffered none — the common case by far,
   * and not worth a request. `undefined` is not `null`: a row that arrived over
   * the socket carries no draft key at all, and "nobody has said" must be asked.
   */
  useEffect(() => {
    // A Reply all or a Forward COMPOSED this box out of the thread when it
    // opened — the audience, the `Fwd:` title, the quoted original. A draft
    // arriving a round trip later must not undo that: `dirtyRef` cannot tell,
    // because an opening is baked into the signature the composer mounted
    // holding and so never looks like typing. What the buffer held is already
    // folded in above (see openingState); the server's copy loses this race by
    // design, exactly as it does against a sentence being typed.
    if (openMode === 'replyAll' || openMode === 'forward') return;
    // `stored`, not `buffered`: a row this composer is dropping is still a row
    // worth asking about, because the server may be holding the same one.
    if (conversation.draft === null && !stored) return;
    let cancelled = false;
    void api
      .getDraft(conversation.id)
      .then(({ draft: server }) => {
        if (cancelled || dirtyRef.current) return;
        const winner = newerDraft(server, buffered);
        if (!winner || winner === buffered) return;
        if (winner.subject && !writtenForward(winner)) {
          // A forward nobody wrote in, left on the server by an older build. It
          // would open a `Fwd:` addressed to nobody here and it counts in the
          // Drafts view, so it goes rather than being restored and ignored.
          void api.saveDraft(conversation.id, clearedBody(winner.isNote)).catch(() => {});
          setDraftSummary(conversation.id, null);
          return;
        }
        setText(winner.content);
        setRich({ html: winner.bodyHtml ?? escapeToHtml(winner.content), text: winner.content });
        setIsNote(winner.isNote);
        setAttachments(winner.attachments);
        const restored = {
          to: winner.toEmails ?? [],
          cc: winner.ccEmails ?? [],
          bcc: winner.bccEmails ?? [],
        };
        setRecipients(restored);
        // The button still outranks the draft about what this box IS: an agent
        // who pressed Reply gets a reply, with the forward's words in it rather
        // than a forward addressed to nobody. Same rule as openingState.
        const asForward = openMode !== 'reply' && writtenForward(winner);
        setMode(asForward ? 'forward' : 'reply');
        setForwardSubjectLine(asForward ? (winner.subject ?? '') : '');
        // Open the panel when the winning draft was addressed to somebody
        // beyond the contact — otherwise a Cc typed yesterday would be sent
        // today by an agent who never saw it.
        if (winner.subject || restored.cc.length || restored.bcc.length) setRecipientsOpen(true);
      })
      .catch(() => {
        // Offline, or a channel this agent may no longer reply on. What this
        // browser buffered is already on screen, and the next save tries again.
      });
    return () => {
      cancelled = true;
    };
    // Mount only. The conversation is fixed for this instance (ThreadPane keys
    // it by conversation), and re-running would fight the agent's typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Keep it, twice over.
   *
   * The buffer is written on every keystroke, because a closed tab or a lost
   * connection never gets the chance to flush; the row summary goes with it, so
   * the conversation list says "Draft" while the words are still being typed.
   * The server save is debounced — it is a round trip, not a keystroke log.
   */
  const pending = useRef<{ displayId: number; body: DraftBody } | null>(null);
  const flush = useRef(() => {});
  useEffect(() => {
    flush.current = () => {
      const job = pending.current;
      if (!job) return;
      pending.current = null;
      // Fire and forget: the buffer still holds it, and a failed save is
      // retried by the next keystroke, or the next time this thread is opened.
      void api.saveDraft(job.displayId, job.body).catch(() => {});
    };
  });

  useEffect(() => {
    // Nothing here is the agent's, and nothing of theirs has been: a composer
    // that has only just opened, or a Forward pressed and not yet written in.
    // Neither is a draft. The local row goes, and NOTHING is said to the server
    // — it may be holding a draft written at another desk that the load above
    // has not answered for yet, and a blank body would delete it.
    //
    // `touchedRef` as well as `dirty`, because a box a message has just left,
    // or one the agent emptied by hand, is untouched by the signature too, and
    // returning here for those is what left the sent words standing: the list
    // still said "Draft" (the summary set while typing was never taken back),
    // the save the debounce had not yet flushed went to the server on unmount —
    // AFTER the send had deleted the row — and a row emptied by hand was never
    // deleted anywhere. A draft that survives its own message is one an agent
    // sends twice. Those two fall through to the clear below, told the way it
    // always is, by a blank body (draftSave deletes on one).
    if (!dirty && !touchedRef.current && !stored && unwritten) {
      saveComposerDraft(draftKey, null);
      return;
    }
    saveComposerDraft(draftKey, unwritten ? null : draft);
    setDraftSummary(conversation.id, unwritten ? null : draft);
    if (!dirty && !touchedRef.current && !stored) return;
    pending.current = { displayId: conversation.id, body: unwritten ? clearedBody(isNote) : draft };
    const timer = setTimeout(() => flush.current(), SAVE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [draftKey, draft, dirty, stored, unwritten, isNote, conversation.id]);

  /**
   * Leaving the thread mid-sentence is the whole reason this feature exists, so
   * an unmount does not wait out the debounce — it sends what is pending now.
   */
  useEffect(() => () => flush.current(), []);

  /**
   * The window closes with the clock, not with a re-render, so the composer polls: an
   * agent sitting on a quiet thread would otherwise keep writing into a window Meta
   * stopped accepting minutes ago. The first tick is a timeout rather than a call in
   * the effect body so this stays out of render.
   */
  const [now, setNow] = useState(0);
  useEffect(() => {
    if (!windowedChannel) return;
    const tick = () => setNow(Date.now());
    const first = setTimeout(tick, 0);
    const timer = setInterval(tick, 30_000);
    return () => {
      clearTimeout(first);
      clearInterval(timer);
    };
  }, [windowedChannel]);

  /**
   * WhatsApp only accepts free-form replies within 24h of the contact's last message;
   * after that a template is the only thing that goes through. Closing the composer
   * here beats letting the agent write, send, and read the failure afterwards.
   */
  const windowClosed = useMemo(() => {
    if (!windowedChannel || isNote) return false;
    if (!lastInboundAt) return true;
    if (!now) return false; // before the first tick, assume open rather than block
    // The window rides in the reduced payload precisely so an agent below Manage
    // can be shown a closed composer; 24 is Meta's own figure and the server's
    // default, and is the only sane answer if a future payload drops the field.
    return now > new Date(lastInboundAt).getTime() + windowHours * 60 * 60 * 1000;
  }, [windowedChannel, windowHours, isNote, lastInboundAt, now]);

  /**
   * The channel stopped being authorized upstream. Every adapter's `canReply`
   * refuses on this, and the refusal lands in the SEND WORKER — so a reply
   * written here is accepted, appears in the thread, and turns red a second
   * later with a provider error under it. Closing the box is the same trade the
   * WhatsApp window makes above: better to say it before it is typed.
   *
   * Notes are exempt and deliberately so. A private note never reaches the
   * provider, and a dead channel is exactly when an agent needs to leave one
   * ("customer called, line is down") — this is the one composer state where the
   * note toggle is the way through rather than a detour.
   */
  const authLapsed = !!inbox && !isNote && needsReauth(inbox);

  const templateIncomplete = !!template && missingFields(template.summary, template.draft).length > 0;
  const hasBody = body.trim().length > 0 || attachments.length > 0;
  const canSubmit =
    !sending &&
    !uploading &&
    !templateIncomplete &&
    !authLapsed &&
    // A template carries its own approved text, so nothing has to be typed alongside it.
    (template ? true : hasBody && !windowClosed);

  /** Replace the lists wholesale, remounting the fields — see `recipientsKey`. */
  const resetRecipients = (next: RecipientLists) => {
    setRecipients(next);
    setRecipientsKey((key) => key + 1);
  };

  /**
   * Open the panel with the thread's own recipient already in the To box.
   *
   * An empty To has always MEANT the contact — the server fills it in — but a
   * box that says nothing while a line underneath explains where the mail is
   * really going asks the agent to hold two facts at once, and reads as a form
   * they forgot to fill in. A chip is the same answer said once, in the place
   * they are looking, and it is what every mail client does.
   *
   * Only when the box is empty, and never on a forward: a forward opens
   * deliberately addressed to nobody, because its whole point is that it goes
   * somewhere else. From then on the list is the agent's — including emptying
   * it again, which brings the note back.
   */
  const openRecipients = () => {
    if (!forwarding && !recipients.to.length && contactEmail) {
      resetRecipients({ ...recipients, to: [contactEmail] });
    }
    setRecipientsOpen(true);
  };

  /** Turn the composer into a forward — see forwardDraft for the shape. */
  const startForward = () => {
    const forward = forwardDraft(replyTargets, threadSubject, fmtDateTime, styled ? rich.text : text);
    setInsertedQuote(forward.body);
    setMode('forward');
    setForwardSubjectLine(forward.subject);
    resetRecipients(forward.lists);
    setRecipientFocus('to');
    setRecipientsOpen(true);
    setTemplate(null);
    setText(forward.body);
    setRich({ html: escapeToHtml(forward.body), text: forward.body });
  };

  const cancelForward = () => {
    setMode('reply');
    setForwardSubjectLine('');
    resetRecipients(emptyLists());
    setRecipientsOpen(false);
    // And take the quoted original back out. startForward put it there; leaving
    // it behind hands the customer their own message back under a button that
    // now reads Reply. Only from the marker down, so anything typed above it
    // survives — the forward flattened the box to text when it opened, which is
    // why rebuilding the html from that text loses nothing that predates it.
    const kept = withoutForwardQuote(body);
    setText(kept);
    setRich({ html: escapeToHtml(kept), text: kept });
  };

  /**
   * Send. `sendMode` is the button that was pressed, not a stored setting —
   * Reply all folds the thread's other recipients into whatever the agent
   * already typed rather than replacing it, so a Cc added by hand survives the
   * click.
   */
  const doSend = async (sendMode: SendMode, alsoClose: boolean) => {
    if (!canSubmit) return;
    const lists =
      sendMode === 'replyAll'
        ? {
            ...recipients,
            cc: dedupeAddresses(
              [...recipients.cc, ...replyRecipients(replyTargets, 'replyAll').cc],
              [...effectiveTo, ...recipients.bcc],
            ),
          }
        : recipients;
    if (sendMode === 'forward' && !lists.to.length) {
      setError('Add at least one recipient to forward this to.');
      setRecipientsOpen(true);
      return;
    }
    setSending(true);
    setError(null);
    try {
      const sendingTemplate = template && !isNote;
      // Only ever attached where the channel can act on them. A note is never
      // delivered and an SMS has one recipient, so putting these on either would
      // store a promise the send path does not keep.
      const mailFields =
        addressable && !sendingTemplate
          ? {
              ...(lists.to.length ? { toEmails: lists.to } : {}),
              ...(lists.cc.length ? { ccEmails: lists.cc } : {}),
              ...(lists.bcc.length ? { bccEmails: lists.bcc } : {}),
              ...(sendMode === 'forward' && forwardSubjectLine
                ? { subject: forwardSubjectLine }
                : {}),
            }
          : {};
      const contentAttributes = sendingTemplate
        ? { templateParams: toTemplateParams(template.summary, template.draft) }
        : Object.keys(mailFields).length
          ? mailFields
          : undefined;
      await onSend({
        // The plain text stays the fallback: previews, notifications, and the
        // text/plain part of the mail all read it. For a template it is the rendered
        // body, which the delivery worker overwrites with what Meta actually accepted.
        content: sendingTemplate ? renderPreview(template.summary, template.draft) : body.trim(),
        ...(styled && rich.text.trim() ? { bodyHtml: rich.html } : {}),
        isNote,
        attachments,
        contentAttributes,
      });
      setText('');
      setRich({ html: '', text: '' });
      setAttachments([]);
      setTemplate(null);
      resetRecipients(emptyLists());
      setRecipientsOpen(false);
      setMode('reply');
      setForwardSubjectLine('');
      if (alsoClose) await onSendAndClose();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Sending failed — try again.');
    } finally {
      setSending(false);
    }
  };

  const pickFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    setError(null);
    try {
      for (const file of Array.from(files)) {
        const { s3Key, putUrl } = await api.presignUpload(
          file.name,
          file.type || 'application/octet-stream',
        );
        await api.putToS3(putUrl, file);
        setAttachments((current) => [
          ...current,
          {
            s3Key,
            fileName: file.name,
            fileType: fileType(file),
            fileSize: file.size,
            extension: file.name.split('.').pop() ?? '',
          },
        ]);
      }
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Upload failed — try again.');
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const suggestReply = async () => {
    setAiLoading(true);
    setError(null);
    try {
      const { draft } = await api.draftReply(conversation.id);
      setText(draft);
      setRich({ html: escapeToHtml(draft), text: draft });
      setTemplate(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not draft a reply.');
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <div className="cmp">
      <div className="cmp-row">
        <span className="cmp-via" style={{ background: meta.muted, color: meta.color }}>
          <ChannelIcon size={13} />
          {isNote ? 'Internal note' : `Reply via ${meta.label}`}
        </span>
        <div className="cmp-picker">
          <button
            type="button"
            className="cmp-tool"
            onClick={() => setMenuOpen(!menuOpen)}
            disabled={isNote}
          >
            <FileText size={13} />
            Template
          </button>
          {menuOpen && (
            <div className="cmp-menu" ref={menuRef}>
              {canned.length > 0 && offeredBlocks.length > 0 && (
                <div className="cmp-menu-group">Canned replies</div>
              )}
              {canned.map((response) => (
                <button
                  key={`canned-${response.id}`}
                  type="button"
                  onClick={() => {
                    setText(response.content);
                    setRich({ html: escapeToHtml(response.content), text: response.content });
                    setTemplate(null);
                    setMenuOpen(false);
                  }}
                >
                  <span className="cmp-menu-title">/{response.shortCode}</span>
                  <span className="cmp-menu-sub">{response.content}</span>
                </button>
              ))}
              {offeredBlocks.length > 0 && <div className="cmp-menu-group">Blocks</div>}
              {offeredBlocks.map((block) => (
                <button
                  key={`block-${block.id}`}
                  type="button"
                  title={block.hint || 'Saved in Templates → Components'}
                  // A block is added to what the agent wrote — a signature, a
                  // letterhead — where a canned reply REPLACES it. Keep the caret
                  // where it is so it lands there and not at the top.
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => {
                    bodyEditor.current?.insertHtml(block.html);
                    setTemplate(null);
                    setMenuOpen(false);
                  }}
                >
                  <span className="cmp-menu-title">{block.name}</span>
                  <span className="cmp-menu-sub">{block.hint || 'Saved email block'}</span>
                </button>
              ))}
              {waTemplates.length > 0 && (canned.length > 0 || offeredBlocks.length > 0) && (
                <div className="cmp-menu-group">WhatsApp templates</div>
              )}
              {waTemplates.map((wa) => (
                <button
                  key={`wa-${wa.name}-${wa.language}`}
                  type="button"
                  title={`WhatsApp template · ${wa.language}`}
                  onClick={() => {
                    // The approved body is the message; anything typed alongside it is
                    // never delivered, so the editor is cleared rather than prefilled.
                    setText('');
                    setRich({ html: '', text: '' });
                    setTemplate({ summary: wa, draft: emptyDraft(wa) });
                    setMenuOpen(false);
                  }}
                >
                  <span className="cmp-menu-title">WhatsApp · {wa.name}</span>
                  <span className="cmp-menu-sub">{wa.bodyText}</span>
                </button>
              ))}
              {canned.length === 0 && offeredBlocks.length === 0 && waTemplates.length === 0 && (
                <div className="cmp-menu-empty">No templates yet.</div>
              )}
            </div>
          )}
        </div>
        <button
          type="button"
          className="cmp-tool"
          onClick={() => void suggestReply()}
          disabled={aiLoading || isNote}
        >
          <Sparkles size={13} />
          {aiLoading ? 'Drafting…' : 'Suggest reply'}
        </button>
        <button
          type="button"
          className={`cmp-tool${isNote ? ' on' : ''}`}
          // The two editors hold separate state; carry the draft across so
          // flipping to a note does not look like it swallowed what was typed.
          onClick={() => {
            if (caps.richText) {
              if (isNote) setRich({ html: escapeToHtml(text), text });
              else setText(rich.text);
            }
            // A note is never delivered, so a picked template has nothing to do with
            // it — and leaving one selected would let an empty note through.
            setTemplate(null);
            setIsNote(!isNote);
          }}
        >
          <Tag size={13} />
          Internal note
        </button>
        {addressable && (
          <button
            type="button"
            className={`cmp-tool${recipientsOpen ? ' active' : ''}`}
            onClick={() => {
              setRecipientFocus(recipients.cc.length || !recipientsOpen ? 'cc' : 'to');
              if (!recipientsOpen) openRecipients();
              else setRecipientsOpen(false);
            }}
            title="To, Cc and Bcc for this reply"
          >
            <Users size={13} />
            Recipients
            {extraCount > 0 && <span className="cmp-tool-count">{extraCount}</span>}
          </button>
        )}
        {sendingAs && !isNote && <span className="cmp-sending-as">Sending as {sendingAs}</span>}
      </div>

      {addressable && recipientsOpen && (
        <>
          {forwarding && (
            <div className="cmp-rcpt-subject">
              <span className="cmp-rcpt-label">Subject</span>
              <input
                value={forwardSubjectLine}
                onChange={(e) => setForwardSubjectLine(e.target.value)}
                placeholder="Subject"
              />
            </div>
          )}
          <RecipientFields
            key={recipientsKey}
            value={recipients}
            onChange={setRecipients}
            focus={recipientFocus}
          />
          {!forwarding && !recipients.to.length && contactEmail && (
            // The To field being empty is not "nobody" — the server addresses the
            // contact. Saying so beats an empty box that reads like a mistake.
            <div className="cmp-rcpt-note">
              Goes to <b>{contactEmail}</b> unless you name someone else above.
            </div>
          )}
        </>
      )}

      {template && (
        <WhatsappTemplatePanel
          template={template.summary}
          draft={template.draft}
          onChange={(draft) => setTemplate({ summary: template.summary, draft })}
          onCancel={() => setTemplate(null)}
        />
      )}
      {/* Above the WhatsApp window notice and instead of it: a template reopens a
          closed window, nothing reopens a channel that cannot authenticate. */}
      {authLapsed && inbox && (
        <div className="cmp-window-closed">
          <b>{identityLine(inbox)} needs reconnecting.</b> It lost its authorization, so replies on
          it will not be delivered. {reauthFixHint(inbox)} You can still leave an internal note.
        </div>
      )}
      {windowClosed && !authLapsed && !template && waChannel && (
        <div className="cmp-window-closed">
          The 24-hour WhatsApp reply window has closed. Pick a template to message this contact
          again.
        </div>
      )}
      {windowClosed && !authLapsed && metaChannel && (
        <div className="cmp-window-closed">
          The {windowHours === 168 ? '7-day' : '24-hour'}{' '}
          {inbox?.channelType === 'instagram' ? 'Instagram' : 'Messenger'} reply window has closed —
          Meta only delivers replies while the customer has written recently. It reopens on their next
          message; you can still leave an internal note.
        </div>
      )}

      {/* The ceiling both editors grow to, as the one number the stylesheet
          reads — `--rt-max` is also what the styled editor is capped by, and a
          second copy in CSS would size a WhatsApp box like an email the first
          time either number moved. */}
      <div
        className={`cmp-box${isNote ? ' note-mode' : ''}`}
        style={{ '--rt-max': `${maxBodyHeight}px` } as CSSProperties}
      >
        {styled ? (
          <RichText
            ref={bodyEditor}
            value={rich}
            onChange={setRich}
            onSubmit={() => void doSend(mode, false)}
            disabled={sending}
            placeholder={`Write your ${meta.label} message…`}
          />
        ) : (
        <textarea
          ref={bodyBox}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') void doSend(mode, false);
          }}
          // Text typed next to a template is never delivered — Meta sends the approved
          // body — so the box is closed rather than quietly discarded on send.
          disabled={!!template}
          // One line high means one line of prompt: a wrapped placeholder is a
          // clipped one at the width the dashboard leaves this module. What the
          // longer wording used to carry is said by something that does not
          // vanish on the first keystroke — the segment counter beside Send, and
          // the lit "Internal note" chip over an orange box.
          placeholder={
            template
              ? 'The template above is what gets sent.'
              : isNote
                ? 'Write an internal note…'
                : isSmsLike
                  ? 'Write your SMS…'
                  : `Write your ${meta.label === 'All' ? '' : `${meta.label} `}message…`
          }
          rows={1}
        />
        )}
        <div className="cmp-bottom">
          {attachments.map((attachment) => (
            <span key={attachment.s3Key} className="cmp-attachment">
              <Paperclip size={11} />
              <span>{attachment.fileName}</span>
              <button
                type="button"
                onClick={() =>
                  setAttachments((current) => current.filter((a) => a.s3Key !== attachment.s3Key))
                }
                title="Remove"
              >
                <X size={11} />
              </button>
            </span>
          ))}
          {caps.attachments && (
            <>
              <button
                type="button"
                className="cmp-iconbtn"
                onClick={() => fileInput.current?.click()}
                disabled={uploading}
                title={uploading ? 'Uploading…' : 'Attach files'}
              >
                <Paperclip size={15} />
              </button>
              <input
                ref={fileInput}
                type="file"
                multiple
                hidden
                onChange={(e) => void pickFiles(e.target.files)}
              />
            </>
          )}
          {!styled && (
            <div className="cmp-emoji-wrap">
              <button
                type="button"
                className="cmp-iconbtn"
                title="Emoji"
                onClick={() => setPlainEmoji((open) => !open)}
              >
                <Smile size={15} />
              </button>
              {plainEmoji && (
                <div className="rt-emoji">
                  {EMOJI.map((emoji) => (
                    <button
                      key={emoji}
                      type="button"
                      onClick={() => {
                        setText((current) => current + emoji);
                        setPlainEmoji(false);
                      }}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {error && <span className="cmp-count over">{error}</span>}
          <div style={{ flex: 1 }} />
          {count && <span className={`cmp-count${count.over ? ' over' : ''}`}>{count.label}</span>}
          {!isNote && !forwarding && (
            <button
              type="button"
              className="cbtn"
              onClick={() => void doSend('reply', true)}
              disabled={!canSubmit}
            >
              {addressable ? 'Reply & close' : 'Send & close'}
            </button>
          )}
          {forwarding && (
            <button type="button" className="cbtn" onClick={cancelForward} disabled={sending}>
              Cancel
            </button>
          )}
          {/* Offered only when there IS somebody else on the thread, and titled
              with their addresses: a button that reveals who it would copy is
              the confirmation, because the click itself sends. */}
          {canReplyAll && (
            <button
              type="button"
              className="cmp-send secondary"
              onClick={() => void doSend('replyAll', false)}
              disabled={!canSubmit}
              title={`Also copies ${replyRecipients(replyTargets, 'replyAll').cc.join(', ')}`}
            >
              <ReplyAll size={14} />
              Reply all
            </button>
          )}
          {addressable && !forwarding && (
            <button
              type="button"
              className="cmp-send secondary"
              onClick={startForward}
              disabled={sending}
              title="Send this thread on to somebody else"
            >
              <Forward size={14} />
              Forward
            </button>
          )}
          <button
            type="button"
            className={`cmp-send${isNote ? ' note-mode' : ''}`}
            onClick={() => void doSend(forwarding ? 'forward' : 'reply', false)}
            disabled={!canSubmit}
          >
            {isNote ? (
              <Send size={14} />
            ) : forwarding ? (
              <Forward size={14} />
            ) : addressable ? (
              <CornerUpLeft size={14} />
            ) : (
              <Send size={14} />
            )}
            {sending
              ? 'Sending…'
              : isNote
                ? 'Add note'
                : forwarding
                  ? 'Forward'
                  : addressable
                    ? 'Reply'
                    : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
}

/** The design's read-only strip shown in place of the composer. */
/**
 * `reason` names WHY this particular thread cannot be replied to. With per-channel
 * grants the answer differs between two conversations on the same screen — "your
 * role" is the wrong explanation for "you have view-only access to Sales".
 */
/**
 * The assistant owns this conversation, so the composer is closed rather than
 * armed. A reply used to be possible and took the thread over silently — which
 * meant an agent answering "just this once" quietly switched the assistant off
 * for that customer, with nothing on screen saying so. Taking over is now the
 * deliberate act it always was in effect.
 */
export function ComposerBotOwned({ onTakeOver, busy }: { onTakeOver: () => void; busy?: boolean }) {
  return (
    <div className="cmp-readonly cmp-bot-owned">
      <Bot size={15} />
      <span>
        <b>The assistant is answering this conversation.</b> Take it over to reply yourself — it
        stops answering the moment you do.
      </span>
      <button type="button" className="cbtn cbtn-sm" disabled={busy} onClick={onTakeOver}>
        Take over
      </button>
    </div>
  );
}

export function ComposerReadOnly({ reason }: { reason?: string | null }) {
  return (
    <div className="cmp-readonly">
      <Lock size={15} />
      {reason ?? 'Replying is disabled for your role.'} You can still read, assign and tag
      conversations.
    </div>
  );
}
